// Crew Settings main component - tab state management, data fetching, role-based tab visibility
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter, useRouteMatch } from 'react-router-dom';
import moment from 'moment';
import { toast } from 'react-toastify';

import { ErrorToast } from 'components';
import IdClientService from 'api/client/client';
import { setClientInformation } from 'redux/generalState.actions';
import history from '../../../history';

import CrewSettingsHeader from './crew-settings-header';
import CrewSettingsAccountTab from './crew-settings-account-tab';
import CrewSettingsDepartmentTab from './crew-settings-department-tab';
import CrewSettingsUserTab from './crew-settings-user-tab';
import CrewSettingsClientTab from './crew-settings-client-tab';

// Map URL path segment → tab id
const PATH_TAB_MAP = {
  '/settings/departments': 'departments',
  '/settings/users':       'users',
  '/settings/clients':     'clients',
};

const getTabFromPath = (pathname) => {
  for (const [seg, tab] of Object.entries(PATH_TAB_MAP)) {
    if (pathname.includes(seg)) return tab;
  }
  return 'account';
};

const TAB_PATHS = {
  account:     '/settings',
  departments: '/settings/departments',
  users:       '/settings/users',
  clients:     '/settings/clients',
};

const CrewSettings = () => {
  const dispatch = useDispatch();

  const clientInfo   = useSelector((state) => state.general.clientInformation);
  const userInfo     = useSelector((state) => state.general.user);
  const userRightsObj = useSelector((state) => state.general?.userRightsObj);

  const [isLoading, setIsLoading]       = useState(false);
  const [users, setUsers]               = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [activeTab, setActiveTab]       = useState(() => getTabFromPath(history.location.pathname));

  const matchPath = useRouteMatch('/settings/clients/:id');

  useEffect(() => {
    fetchClient();
    fetchClientUsers();
  }, []);

  // Sync tab to URL on mount if path changes externally
  useEffect(() => {
    setActiveTab(getTabFromPath(history.location.pathname));
  }, [history.location.pathname]);

  const fetchClient = async () => {
    try {
      setIsLoading(true);
      const { data: { client } } = await IdClientService.getCurrentClient();
      dispatch(setClientInformation(client));
    } catch (error) {
      toast.error(<ErrorToast error={error} />);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchClientUsers = async (cliId) => {
    try {
      const clientId = cliId || localStorage.getItem('BR_CLIENT_ID');
      const { data: { users } } = await IdClientService.getClientUsers(clientId);
      setUsers(users);
    } catch (error) {
      toast.error(<ErrorToast error={error} />);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleTabChange = (tabId) => {
    const path = TAB_PATHS[tabId] || '/settings';
    window.history.pushState({ path }, '', path);
    setActiveTab(tabId);
  };

  // Determine which tabs are visible based on role/rights
  const visibleTabs = [];
  const isAdmin = userInfo?.metadata?.role === 'admin';
  const isRiverr = userInfo?.email?.includes('@riverr.ai');

  if (isAdmin) visibleTabs.push('account');
  if (isAdmin || userRightsObj?.departments?.writeDepts?.length) visibleTabs.push('departments');
  if (isAdmin || userRightsObj?.users?.writeDepts?.length) visibleTabs.push('users');
  if (isRiverr) visibleTabs.push('clients');

  // Fallback: if active tab becomes invisible, reset to first visible
  const resolvedTab = visibleTabs.includes(activeTab) ? activeTab : (visibleTabs[0] || 'account');

  const createdDate = clientInfo?.createdAt
    ? moment(clientInfo.createdAt).format('DD-MM-YYYY')
    : null;

  return (
    <div className="tw-min-h-screen tw-bg-gray-50">
      <div className="tw-max-w-5xl tw-mx-auto">
        <CrewSettingsHeader
          clientName={clientInfo?.name}
          createdDate={createdDate}
          activeTab={resolvedTab}
          onTabChange={handleTabChange}
          visibleTabs={visibleTabs}
        />

        <div className="tw-mt-0">
          {resolvedTab === 'account' && (
            <CrewSettingsAccountTab
              clientData={clientInfo?._id ? clientInfo : null}
              onClientUpdated={fetchClient}
              isLoading={isLoading}
            />
          )}
          {resolvedTab === 'departments' && <CrewSettingsDepartmentTab />}
          {resolvedTab === 'users' && (
            <CrewSettingsUserTab
              users={users}
              fetchUsers={fetchClientUsers}
            />
          )}
          {resolvedTab === 'clients' && (
            <CrewSettingsClientTab matchPath={matchPath} />
          )}
        </div>
      </div>
    </div>
  );
};

export default withRouter(CrewSettings);
